import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  Debt,
  DebtAttachment,
  DebtPayment,
  Organization,
} from '@nargus/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  BellRing,
  Check,
  ChevronLeft,
  Copy,
  LoaderCircle,
  Paperclip,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { useSession } from '@/resources/auth/auth.service';
import {
  debtFileLoader,
  useAddDebtFile,
  useDeleteDebt,
  useDownloadReceipt,
  useNudgeDebt,
  useReceiptUrl,
  useRemoveDebtFile,
  useRemovePayment,
} from '@/resources/debt/debt.service';
import { MAX_FILE_BYTES, canPreview } from '@/lib/files';
import { FileGallery, FilePreviewDialog } from '@/components/file-gallery';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DebtDialog } from './debts-section';
import { PaymentDialog } from './debt-payment-dialog';
import { ConfirmDialog, ErrorNote, EmptyNote, formatDate, formatEuro } from './ui';

export function DebtDetailSkeleton() {
  return (
    <div className="flex max-w-lg flex-col gap-4">
      <Skeleton className="h-8 w-40" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

export function DebtDetail({
  organization,
  debt,
}: {
  organization: Organization;
  debt: Debt;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [paying, setPaying] = useState<{
    payment?: DebtPayment;
    suggestedCents?: number;
  } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const remove = useDeleteDebt();
  const nudge = useNudgeDebt();
  const { user } = useSession();

  const settled = Boolean(debt.settledAt);
  const debtorName =
    debt.debtor && debt.debtorId !== user?.id
      ? (debt.debtor.name ?? debt.debtor.email)
      : null;

  return (
    <div className="flex max-w-lg flex-col gap-4">
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 self-start text-muted-foreground"
        render={
          <Link
            to="/organizations/$organizationId/debts"
            params={{ organizationId: organization.id }}
          />
        }
      >
        <ChevronLeft />
        {t('debts.backToList')}
      </Button>

      <Overview debt={debt} person={debtorName} />

      {!settled && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() =>
                setPaying({ suggestedCents: debt.nextDueCents ?? debt.remainingCents })
              }
            >
              <Plus />
              {t('debts.paymentAddTitle')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setPaying({ suggestedCents: debt.remainingCents })}
            >
              <Check />
              {t('debts.payRest', { amount: formatEuro(debt.remainingCents) })}
            </Button>
            {debtorName && (
              <Button
                variant="outline"
                disabled={nudge.isPending || nudge.isSuccess}
                onClick={() =>
                  nudge.mutate({ organizationId: organization.id, debtId: debt.id })
                }
              >
                {nudge.isPending ? (
                  <LoaderCircle className="size-4 animate-spin" />
                ) : (
                  <BellRing />
                )}
                {nudge.isSuccess
                  ? t('debts.nudgeSent', { name: debtorName })
                  : t('debts.nudge', { name: debtorName })}
              </Button>
            )}
          </div>
          <ErrorNote error={nudge.error} />
        </div>
      )}

      <Payments
        organization={organization}
        debt={debt}
        onEdit={(payment) => setPaying({ payment })}
      />

      <Documents organization={organization} debt={debt} />

      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setEditing(true)}>
          <Pencil />
          {t('debts.editTitle')}
        </Button>
        <Button variant="destructive" onClick={() => setConfirmRemove(true)}>
          <Trash2 />
          {t('debts.removeDebt')}
        </Button>
      </div>

      {editing && (
        <DebtDialog
          organization={organization}
          debt={debt}
          onClose={() => setEditing(false)}
        />
      )}

      {paying && (
        <PaymentDialog
          organization={organization}
          debt={debt}
          payment={paying.payment}
          suggestedCents={paying.suggestedCents}
          onClose={() => setPaying(null)}
        />
      )}

      {confirmRemove && (
        <ConfirmDialog
          title={t('debts.removeDebtTitle')}
          description={t('debts.removeDebtHint', { creditor: debt.creditor })}
          confirmLabel={t('common.delete')}
          busy={remove.isPending}
          error={remove.error}
          onClose={() => setConfirmRemove(false)}
          onConfirm={() =>
            remove.mutate(
              { organizationId: organization.id, debtId: debt.id },
              {
                onSuccess: () =>
                  void navigate({
                    to: '/organizations/$organizationId/debts',
                    params: { organizationId: organization.id },
                  }),
              },
            )
          }
        />
      )}
    </div>
  );
}

function Overview({ debt, person }: { debt: Debt; person: string | null }) {
  const { t } = useTranslation();
  const planText = usePlanText(debt);
  const percent =
    debt.totalCents > 0
      ? Math.min(100, Math.round((debt.paidCents / debt.totalCents) * 100))
      : 0;
  const overdue =
    !debt.settledAt && debt.nextDueAt
      ? new Date(debt.nextDueAt).getTime() < Date.now()
      : false;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-start justify-between gap-3">
          <span className="min-w-0 wrap-break-word">{debt.creditor}</span>
          {debt.settledAt && <Badge variant="secondary">{t('debts.settledBadge')}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div>
          <p className="text-xs text-muted-foreground">
            {debt.settledAt ? t('debts.summaryTotal') : t('debts.summaryRemaining')}
          </p>
          <p className="font-heading text-2xl font-medium tabular-nums">
            {formatEuro(debt.settledAt ? debt.totalCents : debt.remainingCents)}
          </p>
        </div>

        <div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${percent}%` }}
            />
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground tabular-nums">
            {t('debts.paidOf', {
              paid: formatEuro(debt.paidCents),
              total: formatEuro(debt.totalCents),
            })}
          </p>
        </div>

        <dl className="flex flex-col gap-2 text-sm">
          {person && <Detail label={t('debts.personLabel')} value={person} />}
          <Detail label={t('debts.planLabel')} value={planText} />
          <Detail
            label={t('debts.nextDueLabel')}
            value={
              debt.settledAt
                ? t('debts.settledOn', { when: formatDate(debt.settledAt) })
                : debt.nextDueAt
                  ? `${formatDate(debt.nextDueAt)}${
                      debt.nextDueCents ? ` · ${formatEuro(debt.nextDueCents)}` : ''
                    }`
                  : t('debts.noDue')
            }
            tone={overdue ? 'destructive' : undefined}
          />
          {debt.reference && (
            <Detail label={t('debts.referenceLabel')} value={debt.reference} />
          )}
          {debt.iban && <Iban iban={debt.iban} />}
          {debt.note && <Detail label={t('debts.noteLabel')} value={debt.note} />}
        </dl>
      </CardContent>
    </Card>
  );
}

function usePlanText(debt: Debt): string {
  const { t } = useTranslation();

  if (debt.plan === 'installment' && debt.installmentCents && debt.dueDay) {
    return t('debts.planInstallmentSummary', {
      amount: formatEuro(debt.installmentCents),
      day: debt.dueDay,
    });
  }
  return debt.dueAt ? t('debts.planSingleSummary') : t('debts.planNoAgreement');
}

function Detail({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'destructive';
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd
        className={`min-w-0 wrap-break-word text-right ${
          tone === 'destructive' ? 'text-destructive' : ''
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function Iban({ iban }: { iban: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(iban);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
    }
  }

  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-xs text-muted-foreground">{t('debts.ibanLabel')}</dt>
      <dd className="flex min-w-0 items-center gap-1">
        <span className="truncate font-mono text-sm">{iban}</span>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0"
          aria-label={t('debts.ibanCopy')}
          onClick={() => void copy()}
        >
          {copied ? <Check /> : <Copy />}
        </Button>
      </dd>
    </div>
  );
}

function Payments({
  organization,
  debt,
  onEdit,
}: {
  organization: Organization;
  debt: Debt;
  onEdit: (payment: DebtPayment) => void;
}) {
  const { t } = useTranslation();
  const [confirmRemove, setConfirmRemove] = useState<DebtPayment | null>(null);
  const [preview, setPreview] = useState<DebtPayment | null>(null);
  const remove = useRemovePayment();
  const openReceipt = useDownloadReceipt();
  const ref = { organizationId: organization.id, debtId: debt.id };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('debts.paymentsLabel')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-1">
        {debt.payments.length === 0 ? (
          <EmptyNote>{t('debts.paymentsNone')}</EmptyNote>
        ) : (
          debt.payments.map((payment) => {
            const receipt = payment.receipt;

            return (
              <div key={payment.id} className="flex items-center gap-2 py-1">
                <span className="min-w-0 flex-1 truncate text-sm">
                  {formatDate(payment.paidAt)}
                </span>
                <span className="shrink-0 text-sm font-medium tabular-nums">
                  {formatEuro(payment.amountCents)}
                </span>

                {receipt && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="shrink-0"
                    aria-label={t('debts.receiptOpen')}
                    disabled={openReceipt.isPending}
                    onClick={() =>
                      canPreview(receipt.contentType)
                        ? setPreview(payment)
                        : openReceipt.mutate({
                            ...ref,
                            paymentId: payment.id,
                            filename: receipt.filename,
                          })
                    }
                  >
                    <Paperclip />
                  </Button>
                )}

                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0"
                  aria-label={t('debts.paymentEditTitle')}
                  onClick={() => onEdit(payment)}
                >
                  <Pencil />
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0"
                  aria-label={t('debts.paymentRemove', { when: formatDate(payment.paidAt) })}
                  onClick={() => setConfirmRemove(payment)}
                >
                  <Trash2 />
                </Button>
              </div>
            );
          })
        )}

        <ErrorNote error={openReceipt.error} />

        {preview?.receipt && (
          <ReceiptPreview
            key={preview.id}
            reference={{ ...ref, paymentId: preview.id }}
            receipt={preview.receipt}
            onClose={() => setPreview(null)}
          />
        )}

        {confirmRemove && (
          <ConfirmDialog
            title={t('debts.paymentRemoveTitle')}
            description={t('debts.paymentRemoveHint', {
              amount: formatEuro(confirmRemove.amountCents),
              when: formatDate(confirmRemove.paidAt),
            })}
            confirmLabel={t('common.delete')}
            busy={remove.isPending}
            error={remove.error}
            onClose={() => setConfirmRemove(null)}
            onConfirm={() =>
              remove.mutate(
                { ...ref, paymentId: confirmRemove.id },
                { onSuccess: () => setConfirmRemove(null) },
              )
            }
          />
        )}
      </CardContent>
    </Card>
  );
}

function ReceiptPreview({
  reference,
  receipt,
  onClose,
}: {
  reference: { organizationId: string; debtId: string; paymentId: string };
  receipt: { filename: string; contentType: string };
  onClose: () => void;
}) {
  const { url, error } = useReceiptUrl(reference);
  return (
    <FilePreviewDialog
      filename={receipt.filename}
      contentType={receipt.contentType}
      url={url}
      error={error}
      onClose={onClose}
    />
  );
}

function Documents({
  organization,
  debt,
}: {
  organization: Organization;
  debt: Debt;
}) {
  const { t } = useTranslation();
  const picker = useRef<HTMLInputElement>(null);
  const [tooLarge, setTooLarge] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<DebtAttachment | null>(null);
  const add = useAddDebtFile();
  const remove = useRemoveDebtFile();
  const ref = { organizationId: organization.id, debtId: debt.id };
  const busy = add.isPending;

  function onPick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setTooLarge(file.size > MAX_FILE_BYTES);
    if (file.size > MAX_FILE_BYTES) return;
    add.mutate({ ...ref, file });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('debts.filesLabel')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        <FileGallery
          files={debt.attachments}
          load={debtFileLoader(ref)}
          disabled={busy}
          empty={t('debts.filesNone')}
          onRemove={setConfirmRemove}
        />

        <input id="debt-file" ref={picker} type="file" className="hidden" onChange={onPick} />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          disabled={busy}
          onClick={() => picker.current?.click()}
        >
          {add.isPending ? <LoaderCircle className="size-4 animate-spin" /> : <Upload />}
          {t('debts.fileAdd')}
        </Button>

        <p className="text-xs text-muted-foreground">{t('debts.filesHint')}</p>
        {tooLarge && <p className="text-sm text-destructive">{t('common.fileTooLarge')}</p>}
        <ErrorNote error={add.error} />

        {confirmRemove && (
          <ConfirmDialog
            title={t('debts.fileRemoveTitle')}
            description={t('common.deleteFileHint', { filename: confirmRemove.filename })}
            confirmLabel={t('common.delete')}
            busy={remove.isPending}
            error={remove.error}
            onClose={() => setConfirmRemove(null)}
            onConfirm={() =>
              remove.mutate(
                { ...ref, attachmentId: confirmRemove.id },
                { onSuccess: () => setConfirmRemove(null) },
              )
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
